import { useCallback, useEffect, useMemo, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { fetchNudgeThrottlingState } from '../utils/fetchNudgeThrottlingState';

type UseFetchNudgeThrottlingStateProps = {
	triggerId: string;
	hasNudgeToShow?: boolean;
};

type UseFetchNudgeThrottlingStateReturnType = {
	isNudgeThrottled: boolean;
	isLoading: boolean;
	error?: Error;
};

export const useFetchNudgeThrottlingState = ({
	triggerId,
	hasNudgeToShow,
}: UseFetchNudgeThrottlingStateProps): UseFetchNudgeThrottlingStateReturnType => {
	const [isNudgeThrottled, setIsNudgeThrottled] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const makeNudgeThrottlingStateRequest = useCallback(async () => {
		try {
			const nudgeThrottlingState = await fetchNudgeThrottlingState({ triggerId });
			setIsNudgeThrottled(
				nudgeThrottlingState.triggers[0].recommendations[0].recommendation_details.nudge_throttled,
			);
		} catch (error) {
			setError(error as Error);
		} finally {
			setIsLoading(false);
		}
	}, [triggerId]);

	useEffect(() => {
		if (!hasNudgeToShow) {
			setIsLoading(false);
			return;
		}

		if (FeatureGates.checkGate('rovo_button_nudge_throttling_enabled')) {
			void makeNudgeThrottlingStateRequest();
			return;
		}
	}, [makeNudgeThrottlingStateRequest, hasNudgeToShow]);

	return useMemo(() => {
		const getNudgeThrottlingState = () => {
			if (error || !isNudgeThrottled) {
				return false;
			}
			return isNudgeThrottled;
		};

		return {
			isNudgeThrottled: getNudgeThrottlingState(),
			isLoading,
			error,
		};
	}, [isNudgeThrottled, isLoading, error]);
};
